/**
 * ModeratePerson - suspend or reinstate somebody, from wherever you found them
 * ----------------------------------------------------------------------------
 * Added 18 August 2026, and it closes the gap section 7 of HANDOFF describes:
 * until now every moderator control was a button on a REPORT, so a moderator
 * could only act on something somebody else had already complained about.
 *
 * That is fine for working through the queue and useless for the other half of
 * the job - noticing something yourself. An inappropriate photograph, name or
 * biography is not something a student is likely to report; it is something
 * the moderator sees while looking at the directory. With one moderator who is
 * also the owner, that second case is at least as likely as the first.
 *
 * The workaround was to report somebody yourself and then act on your own
 * report, which works and reads as a bodge.
 *
 * It reuses SuspendDialog UNCHANGED, so a suspension started here is the same
 * suspension in every respect: a reason is required, it is shown to the person
 * word for word every time they try to log in, it signs them out everywhere,
 * and it goes in the audit log. There is deliberately no lighter-touch version
 * for "I just spotted this".
 */

import { useState } from 'react';
import { Ban, RotateCcw } from 'lucide-react';

import { safetyApi } from '../services/api';
import SuspendDialog from './SuspendDialog';
import { useI18n } from '../hooks/useI18n';

export default function ModeratePerson({ person, onChanged }) {
  const { t } = useI18n();
  const [suspending, setSuspending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!person) return null;

  const suspend = async ({ days, indefinite, reason }) => {
    setBusy(true);
    setError('');
    try {
      await safetyApi.suspendUser(person._id, { days, indefinite, reason });
      setSuspending(null);
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const reinstate = async () => {
    setBusy(true);
    setError('');
    try {
      await safetyApi.reinstateUser(person._id);
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {error && (
        <div className="alert alert-error" style={{ marginTop: 'var(--space-xs)' }}>
          {error}
        </div>
      )}

      {person.isActive === false ? (
        <>
          {/* Why they were suspended, in the words whoever did it used. A
              moderator coming back to this a week later - or a second
              moderator - should not have to open the audit log to find out
              what the decision was. */}
          {person.suspendedReason && (
            <p className="text-sm text-muted" style={{ marginTop: 'var(--space-xs)' }}>
              {person.suspendedReason}
            </p>
          )}
          <button
            type="button"
            className="btn btn-outline btn-sm btn-inline"
            onClick={reinstate}
            disabled={busy}
            style={{ marginTop: 'var(--space-xs)' }}
          >
            <RotateCcw size={16} /> {t('moderation.reinstate')}
          </button>
        </>
      ) : (
        <button
          type="button"
          className="btn btn-danger btn-sm btn-inline"
          onClick={() => setSuspending(person)}
          disabled={busy}
          style={{ marginTop: 'var(--space-xs)' }}
        >
          <Ban size={16} /> {t('moderation.suspend')}
        </button>
      )}

      <SuspendDialog
        open={Boolean(suspending)}
        person={suspending}
        busy={busy}
        onClose={() => setSuspending(null)}
        onConfirm={suspend}
      />
    </>
  );
}
